import { useMemo } from "react";
import { addDays, addMonths, addWeeks, parseISO, startOfDay } from "date-fns";
import type { Granularity } from "~/components/charts";
import type { MetadataEvent } from "~/pages/product-details/tabs/sales-data-tab/hooks/use-metadata-events";
import { MetadataEventClusterMarker } from "./metadata-event-cluster-marker";
import { MetadataEventMarker } from "./metadata-event-marker";

const bucketEnd = (start: Date, granularity: Granularity): Date => {
  if (granularity === "monthly") {
    return addMonths(start, 1);
  }
  if (granularity === "weekly") {
    return addWeeks(start, 1);
  }
  return addDays(start, 1);
};

export const MetadataEventOverlay = ({
  buckets,
  events,
  granularity,
  onDrill,
}: {
  buckets: { date: string }[];
  events: MetadataEvent[];
  granularity: Granularity;
  onDrill?: () => void;
}) => {
  const groups = useMemo(() => {
    if (buckets.length === 0 || events.length === 0) {
      return [];
    }

    const bucketStarts = buckets.map((bucket) =>
      startOfDay(parseISO(bucket.date)).getTime(),
    );

    const byBucket = new Map<number, MetadataEvent[]>();
    for (const event of events) {
      const day = startOfDay(event.occurredAt).getTime();
      let index: number;
      if (event.type === "RELEASE_DATE") {
        index = bucketStarts.findIndex((start) => start >= day);
      } else {
        const found = bucketStarts.findLastIndex((start) => start <= day);
        index =
          found !== -1 &&
          day < bucketEnd(new Date(bucketStarts[found]), granularity).getTime()
            ? found
            : -1;
      }
      if (index !== -1) {
        byBucket.set(index, [...(byBucket.get(index) ?? []), event]);
      }
    }

    return [...byBucket.entries()]
      .sort(([a], [b]) => a - b)
      .map(([index, groupEvents]) => {
        const firstDay = startOfDay(groupEvents[0].occurredAt).getTime();
        return {
          xValue: buckets[index].date,
          events: groupEvents,
          sameType: groupEvents.every(
            (event) => event.type === groupEvents[0].type,
          ),
          sameDay: groupEvents.every(
            (event) => startOfDay(event.occurredAt).getTime() === firstDay,
          ),
          periodStart: startOfDay(parseISO(buckets[index].date)),
        };
      });
  }, [buckets, events, granularity]);

  if (groups.length === 0) {
    return null;
  }

  return (
    <>
      {groups.map((group) =>
        group.sameDay && group.sameType ? (
          <MetadataEventMarker
            key={group.xValue}
            xValue={group.xValue}
            events={group.events}
          />
        ) : (
          <MetadataEventClusterMarker
            key={group.xValue}
            xValue={group.xValue}
            events={group.events}
            date={group.periodStart}
            onClick={group.sameDay ? undefined : onDrill}
          />
        ),
      )}
    </>
  );
};
